import { useEffect, useMemo, useRef, useState } from "react";
import "@divkitframework/visual-editor/dist/divkit-editor.css";
import type { DivProEditorInstance } from "@divkitframework/visual-editor";
import type { Asset, AssetPlacement, CreativeAction, CreativeAssignment, CreativeVersion, CreativeVersionCreateCommand, JsonObject, Placement } from "../api/types";
import { buildEditorVersion, DivKitAdapterError, editorDocument, initialDivKitDocument, initialEditorVersion, placementViewportList } from "../creative/divkitAdapter";

export function CreativeBuilder({ placement, versions, assignments, busy, saveError, onClose, onUpload, onSubmit }: {
  placement: Placement;
  versions: CreativeVersion[];
  assignments: CreativeAssignment[];
  busy: boolean;
  saveError: string | null;
  onClose: () => void;
  onUpload: (file: File) => Promise<Asset>;
  onSubmit: (value: CreativeVersionCreateCommand) => void;
}) {
  const initialVersion = useMemo(() => initialEditorVersion(versions, assignments), [assignments, versions]);
  const [sourceVersionId, setSourceVersionId] = useState<string>(initialVersion?.id ?? "");
  const sourceVersion = versions.find(version => version.id === sourceVersionId) ?? null;
  const assignedVersionIds = useMemo(() => new Set(assignments.filter(assignment => assignment.weight > 0).map(assignment => assignment.creativeVersionId)), [assignments]);
  const screen = useRef<HTMLElement>(null);
  const host = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const editor = useRef<DivProEditorInstance | null>(null);
  const [document, setDocument] = useState<unknown>(() => sourceVersion ? editorDocument(sourceVersion.divkit) : initialDivKitDocument());
  const [editorErrors, setEditorErrors] = useState<string[]>([]);
  const [adapterError, setAdapterError] = useState<string | null>(null);
  const [lastUploadedUrl, setLastUploadedUrl] = useState<string | null>(null);
  const [actions, setActions] = useState<CreativeAction[]>(() => structuredClone(sourceVersion?.actions ?? []));
  const [assets, setAssets] = useState<AssetPlacement[]>(() => structuredClone(sourceVersion?.assets ?? []));
  const [disclosure, setDisclosure] = useState<JsonObject>(() => structuredClone(sourceVersion?.disclosure ?? { label: "Реклама" }));
  const disclosureText = typeof disclosure["label"] === "string" ? disclosure["label"] : "Реклама";

  useEffect(() => {
    if (!sourceVersionId && initialVersion) setSourceVersionId(initialVersion.id);
  }, [initialVersion, sourceVersionId]);

  useEffect(() => {
    const dialog = screen.current;
    if (!dialog) return;
    const background = [
      globalThis.document.querySelector<HTMLElement>(".shell > .sidebar"),
      ...Array.from(dialog.parentElement?.children ?? []).filter((node): node is HTMLElement => node instanceof HTMLElement && node !== dialog),
    ].filter((node): node is HTMLElement => Boolean(node));
    for (const node of background) node.inert = true;
    closeButton.current?.focus();
    return () => {
      for (const node of background) node.inert = false;
    };
  }, []);

  useEffect(() => {
    if (!busy || !host.current) return;
    host.current.inert = true;
    closeButton.current?.focus();
    return () => {
      if (host.current) host.current.inert = false;
    };
  }, [busy]);

  useEffect(() => {
    const handleDialogKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented && !busy) {
        onClose();
        return;
      }
      if (event.key !== "Tab" || event.defaultPrevented || !screen.current) return;
      const focusable = Array.from(screen.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
      )).filter(element => !element.inert && element.getAttribute("aria-hidden") !== "true");
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && globalThis.document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && globalThis.document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    window.addEventListener("keydown", handleDialogKey);
    return () => window.removeEventListener("keydown", handleDialogKey);
  }, [busy, onClose]);

  useEffect(() => {
    const seed = sourceVersion ? editorDocument(sourceVersion.divkit) : initialDivKitDocument();
    const sourceActions = structuredClone(sourceVersion?.actions ?? []);
    setDocument(seed);
    setActions(sourceActions);
    setAssets(structuredClone(sourceVersion?.assets ?? []));
    setDisclosure(structuredClone(sourceVersion?.disclosure ?? { label: "Реклама" }));
    setAdapterError(null);
    setEditorErrors([]);

    let disposed = false;
    void import("@divkitframework/visual-editor").then(({ DivProEditor }) => {
      if (disposed || !host.current) return;
      const instance = DivProEditor.init({
        renderTo: host.current,
        value: JSON.stringify(seed),
        locale: "ru",
        theme: "light",
        rootConfigurable: true,
        fitViewportOnCreate: true,
        viewportList: placementViewportList(placement.layout.aspectRatio),
        fileLimits: { image: { error: 20 * 1024 * 1024 }, upload: { error: 20 * 1024 * 1024 } },
        imageConversion: { formats: { png: true, jpg: true } },
        api: {
          uploadFile: async file => {
            const asset = await onUpload(file);
            if (!asset.publicUrl) throw new Error("Сервер не вернул публичную read-ссылку.");
            setLastUploadedUrl(asset.publicUrl);
            return asset.publicUrl;
          },
          onChange: () => {
            try {
              const value = JSON.parse(instance.getValue()) as unknown;
              setDocument(value);
              setEditorErrors(instance.getErrors().map(error => error.message));
              setAdapterError(null);
            } catch (error) {
              setAdapterError(error instanceof Error ? error.message : "DivKit JSON не читается.");
            }
          },
        },
      });
      editor.current = instance;
      instance.setCustomActions(sourceActions.filter(action => action.id.trim()).map(action => ({
        baseUrl: `alot-action://${action.id}`,
        text: { ru: action.id, en: action.id },
      })));
      setDocument(JSON.parse(instance.getValue()) as unknown);
      setEditorErrors(instance.getErrors().map(error => error.message));
    });
    return () => {
      disposed = true;
      editor.current?.destroy();
      editor.current = null;
      if (host.current) host.current.replaceChildren();
    };
  }, [onUpload, placement.layout.aspectRatio, sourceVersionId]);

  useEffect(() => {
    editor.current?.setCustomActions(actions.filter(action => action.id.trim()).map(action => ({
      baseUrl: `alot-action://${action.id}`,
      text: { ru: action.id, en: action.id },
    })));
  }, [actions]);

  const result = useMemo(() => {
    try {
      return buildEditorVersion(document, actions, disclosure, assets);
    } catch (error) {
      return error instanceof DivKitAdapterError ? error : new DivKitAdapterError("DivKit JSON не может быть сохранён.");
    }
  }, [actions, assets, disclosure, document]);
  const validationError = adapterError ?? (result instanceof Error ? result.message : null);
  const saveDisabled = busy || Boolean(validationError) || !disclosureText.trim();
  const save = () => !(result instanceof Error) && onSubmit(result);

  return <section ref={screen} className={`divkit-editor-screen${busy ? " is-saving" : ""}`} role="dialog" aria-modal="true" aria-label="Редактор креатива" aria-busy={busy}>
    <div className="divkit-editor-heading">
      <div><h3>DivKit Visual Editor</h3><p className="muted">Редактор открыт из сохранённой версии. Сохранение всегда создаёт новую неизменяемую версию и не меняет исходную.</p></div>
      <div className="divkit-editor-toolbar">
        <span className="muted">placement {placement.layout.aspectRatio}:1</span>
        <button ref={closeButton} type="button" onClick={onClose} disabled={busy}>Закрыть</button>
        <button type="button" className="primary" disabled={saveDisabled} onClick={save}>{busy ? "Сохранение…" : "Сохранить новую неизменяемую версию"}</button>
      </div>
    </div>
    <div className="field" style={{ padding: "12px 16px 0" }}>
      <label htmlFor="editor-source-version">Исходная версия</label>
      <select id="editor-source-version" disabled={busy} value={sourceVersionId} onChange={event => setSourceVersionId(event.target.value)}>
        {versions.length === 0 && <option value="">Новый пустой документ</option>}
        {versions.slice().sort((left, right) => right.version - left.version).map(version => <option key={version.id} value={version.id}>
          v{version.version} · {version.publicationState}{assignedVersionIds.has(version.id) ? " · назначена кампании" : ""}
        </option>)}
      </select>
      {sourceVersion && <small className="muted">Открыт полный DivKit v{sourceVersion.version}. Изменения будут сохранены как новая версия.</small>}
    </div>
    {saveError && <p className="error divkit-save-error" role="alert">{saveError} Черновик сохранён в редакторе — исправьте данные и повторите.</p>}
    <div ref={host} className="divkit-editor-host" aria-label="DivKit Visual Editor" aria-disabled={busy} />
    <div className="divkit-publication-controls">
      <div>
        <h4>Файлы</h4>
        <p className="muted">PNG, JPEG, GIF и Lottie JSON загружаются в постоянное публичное хранилище. Полученную ссылку можно использовать в любом URL-поле DivKit.</p>
        {lastUploadedUrl && <div className="field"><label>Последняя read-ссылка</label><div className="action-editor"><input readOnly value={lastUploadedUrl} aria-label="Последняя публичная read-ссылка" /><button type="button" onClick={() => navigator.clipboard.writeText(lastUploadedUrl)}>Копировать</button></div></div>}
        <h4>Внешние действия</h4>
        <p className="muted">Действия исходной версии загружаются вместе с DivKit. В DivKit сохраняется URL <code>alot-action://actionId</code>.</p>
        {actions.map((action, index) => <div className="action-editor" key={index}>
          <input disabled={busy} aria-label={`Action ${index + 1} id`} placeholder="actionId" value={action.id} onChange={event => setActions(value => value.map((item, position) => position === index ? { ...item, id: event.target.value } : item))} />
          <select disabled={busy} aria-label={`Action ${index + 1} type`} value={action.kind} onChange={event => setActions(value => value.map((item, position) => position === index ? { ...item, kind: event.target.value as CreativeAction["kind"] } : item))}><option value="partner_click">partner_click</option><option value="disclosure">disclosure</option><option value="copy">copy</option></select>
          {action.kind === "partner_click" && <input disabled={busy} aria-label={`Action ${index + 1} URL`} placeholder="https://…" value={action.url ?? ""} onChange={event => setActions(value => value.map((item, position) => position === index ? { ...item, url: event.target.value } : item))} />}
          <button type="button" disabled={busy} className="danger" aria-label={`Удалить action ${index + 1}`} onClick={() => setActions(value => value.filter((_item, position) => position !== index))}>×</button>
        </div>)}
        <button type="button" disabled={busy} onClick={() => setActions(value => [...value, { id: `action_${value.length + 1}`, kind: "partner_click", url: "" }])}>+ действие</button>
      </div>
      <div>
        <div className="field"><label>Маркировка disclosure</label><input disabled={busy} value={disclosureText} onChange={event => setDisclosure(value => ({ ...value, label: event.target.value }))} /></div>
        {editorErrors.length > 0 && <p className="muted">Предупреждение редактора: {editorErrors[0]}</p>}
        {validationError && <p className="error" role="alert">{validationError}</p>}
        {saveError && <p className="error" role="alert">{saveError} Черновик сохранён в редакторе — исправьте данные и повторите.</p>}
        <button className="primary" disabled={saveDisabled} onClick={save}>{busy ? "Сохранение…" : "Сохранить новую неизменяемую версию"}</button>
      </div>
    </div>
  </section>;
}
